// Editor prompt Seedance có chip tham chiếu (E5-P2).
//
// 🔴 VÌ SAO KHÔNG ĐỂ NGƯỜI DÙNG GÕ "@Image3": `@ImageN` không phải định danh, nó là VỊ TRÍ trong mảng ảnh
// gửi kèm, được tính lại ở mỗi lần gen (faces → outfits → location → props). Gán thêm một DC cho ai đó là
// mọi số phía sau dịch đi — prompt vẫn chạy, engine vẫn trả clip, chỉ là sai người và không có gì báo.
// Đo thật trên dữ liệu: 5/50 prompt sửa tay đang trỏ lệch, có cái để "bối cảnh" trỏ vào một cái áo.
//
// ⇒ Prompt LƯU token bám thực thể `@[Nhãn](char:id)`; số chỉ được render ngay trước khi gửi engine.
// Component này giấu hẳn token: người dùng thấy CHIP MANG ẢNH — cả lúc xem lẫn lúc sửa.
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Tag, Button, Space, Typography, Tooltip, Popover, Input, Empty } from 'antd';
import type { EventRefBank } from '../../types/film';
import {
  RefKind, RefChoice, KIND_META, COLOR_HEX, buildRefChoices, splitPieces, buildToken, domToText,
} from './promptChipUtils';

export type { RefKind, RefChoice };
export { buildRefChoices };

const { Text } = Typography;

interface Props {
  value: string;
  bank?: EventRefBank;
  /** 260927 mục J — engine đang chạy (`plan.engine`); quyết chip nhân vật đọc `hasWornLook` hay `hasDC`. */
  engine?: 'AVIS' | 'BYTEPLUS';
  /** 261010 UI2 (L6a) — chip bổ sung (video tham chiếu của video). */
  extraChoices?: RefChoice[];
  onSave?: (next: string) => void;
  /** 261010 P9D (#29) — chỉ xem: chip có ảnh trong câu, KHÔNG nút «Sửa prompt» (storyboard — I1). */
  readOnly?: boolean;
  /** 261010 P9D (#29) — soạn thẳng (không bước Xem → Sửa → Lưu): luôn ở chế độ sửa, báo chữ mới qua `onChange` mỗi lần đổi; không nút Lưu/Huỷ. Cho ô Custom Prompt (ảnh) nơi nút Gen là nút lưu. */
  inline?: boolean;
  onChange?: (text: string) => void;
  /** Chữ khi prompt rỗng ở chế độ xem. */
  emptyText?: string;
  minHeight?: number;
}

const PromptChipEditor: React.FC<Props> = ({ value, bank, engine, extraChoices, onSave, readOnly, inline, onChange, emptyText, minHeight }) => {
  const [editing, setEditing] = useState(!!inline);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState('');
  const [dirty, setDirty] = useState(false);
  const edRef = useRef<HTMLDivElement>(null);
  /** Caret cuối cùng trong editor — popover chiếm focus nên phải nhớ lại chỗ chèn. */
  const savedRange = useRef<Range | null>(null);

  const choices = useMemo(() => buildRefChoices(bank, engine, extraChoices), [bank, engine, extraChoices]);
  const byId = useMemo(() => new Map(choices.map((c) => [`${c.kind}:${c.id}`, c])), [choices]);
  const pieces = useMemo(() => splitPieces(value), [value]);

  // Prompt cũ chưa chuyển sang token: số cứng sẽ trỏ sai ngay khi bank đổi ⇒ cảnh báo, không im lặng.
  const rawIdx = (value.match(/@(Image|Audio)\d+/g) || []).length;

  /** Dựng một chip DOM (dùng cả lúc nạp nội dung lẫn lúc chèn mới). */
  const makeChipEl = useCallback((kind: RefKind, id: string, label: string): HTMLElement => {
    const known = byId.get(`${kind}:${id}`);
    const meta = KIND_META[kind];
    const color = known ? (known.ready ? COLOR_HEX[meta.color] : '#fa8c16') : '#ff4d4f';
    const span = document.createElement('span');
    span.setAttribute('data-token', buildToken(kind, id, label));
    span.contentEditable = 'false';
    span.title = known
      ? `${meta.label}: ${known.label}${known.ready ? '' : ' — chưa có ảnh, khi gen chỉ còn mô tả bằng chữ'}`
      : 'Không tìm thấy trong bank — thực thể có thể đã bị xoá';
    span.style.cssText = `display:inline-flex;align-items:center;gap:4px;vertical-align:middle;margin:0 2px;`
      + `padding:${known?.previewUrl ? '1px 6px 1px 1px' : '1px 6px'};border:1px solid ${color};border-radius:11px;`
      + `background:${color}14;color:${color};font-size:11px;line-height:1.6;user-select:none;max-width:230px;`;
    if (known?.previewUrl) {
      const img = document.createElement('img');
      img.src = known.previewUrl;
      img.alt = '';
      img.style.cssText = `width:18px;height:18px;border-radius:${kind === 'char' ? '50%' : '3px'};object-fit:cover;flex-shrink:0;background:#fff;`;
      span.appendChild(img);
    } else {
      const ico = document.createElement('span');
      ico.textContent = meta.icon;
      ico.style.fontSize = '10px';
      span.appendChild(ico);
    }
    const txt = document.createElement('span');
    txt.textContent = label;
    txt.style.cssText = 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
    span.appendChild(txt);
    return span;
  }, [byId]);

  /** Nạp text vào editor một lần khi mở chế độ sửa. KHÔNG re-render sau đó — React đụng vào là mất caret. */
  const loadIntoEditor = useCallback((text: string) => {
    const root = edRef.current;
    if (!root) return;
    root.innerHTML = '';
    for (const p of splitPieces(text)) {
      if (p.type === 'text') root.appendChild(document.createTextNode(p.text));
      else root.appendChild(makeChipEl(p.kind, p.id, p.label));
    }
    // Không có node nào (prompt rỗng) → để một text node cho caret bám vào.
    if (!root.childNodes.length) root.appendChild(document.createTextNode(''));
  }, [makeChipEl]);

  // 🔴 Nạp ĐÚNG MỘT LẦN khi vào chế độ sửa — KHÔNG nạp lại khi `value` đổi.
  // Tab này poll reload mỗi 10s trong lúc có clip đang gen; nếu nghe theo `value` thì mỗi lượt poll sẽ
  // dựng lại DOM và cuốn sạch những gì người dùng vừa gõ, ngay giữa chừng.
  const loadedFor = useRef<boolean>(false);
  useEffect(() => {
    if (!editing) { loadedFor.current = false; return; }
    if (loadedFor.current) return;
    loadedFor.current = true;
    loadIntoEditor(value);
  }, [editing, value, loadIntoEditor]);

  const rememberCaret = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && edRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const insert = (c: RefChoice) => {
    const root = edRef.current;
    if (!root) return;
    const chip = makeChipEl(c.kind, c.id, c.label);
    const range = savedRange.current && root.contains(range0Node(savedRange.current))
      ? savedRange.current
      : endRange(root);
    range.deleteContents();

    // Gõ "@" rồi chọn → nuốt luôn ký tự "@" ngay trước caret, khỏi thành "@[chip]".
    const n = range.startContainer;
    if (n.nodeType === Node.TEXT_NODE && range.startOffset > 0 && (n.textContent || '')[range.startOffset - 1] === '@') {
      (n as Text).deleteData(range.startOffset - 1, 1);
      range.setStart(n, range.startOffset - 1);
      range.collapse(true);
    }

    range.insertNode(chip);
    // Một khoảng trắng sau chip để còn chỗ đặt caret và gõ tiếp — không có thì caret kẹt sát chip.
    const space = document.createTextNode(' ');
    chip.parentNode?.insertBefore(space, chip.nextSibling);
    const after = document.createRange();
    after.setStart(space, 1);
    after.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(after);
    savedRange.current = after.cloneRange();

    setPickerOpen(false); setQ(''); setDirty(true);
    if (inline) onChange?.(domToText(root));
    setTimeout(() => root.focus(), 0);
  };

  const picker = (
    <div style={{ width: 320 }}>
      <Input placeholder="Tìm nhân vật / bối cảnh / đạo cụ…" size="small" value={q} onChange={(e) => setQ(e.target.value)} autoFocus allowClear />
      <div style={{ maxHeight: 280, overflow: 'auto', marginTop: 6 }}>
        {(() => {
          const hit = choices.filter((c) => c.label.toLowerCase().includes(q.trim().toLowerCase()));
          if (!hit.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có" style={{ margin: '12px 0' }} />;
          return (['crowd','char', 'look', 'loc', 'prop', 'voice', 'video'] as RefKind[]).map((k) => {
            const g = hit.filter((c) => c.kind === k);
            if (!g.length) return null;
            return (
              <div key={k} style={{ marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 10 }}>{KIND_META[k].icon} {KIND_META[k].label}</Text>
                {g.map((c) => (
                  <div key={`${c.kind}:${c.id}`} onClick={() => insert(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 4px', cursor: 'pointer', borderRadius: 4 }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                    {c.previewUrl
                      ? <img src={c.previewUrl} alt="" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: c.kind === 'char' ? '50%' : 3 }} />
                      : <div style={{ width: 22, height: 22, background: '#f0f0f0', borderRadius: 3 }} />}
                    <Text style={{ fontSize: 12, flex: 1 }}>{c.label}</Text>
                    {!c.ready && <Tooltip title="Chưa có ảnh/asset — chip này sẽ chỉ thành mô tả bằng chữ khi gen"><Tag color="orange" style={{ marginInlineEnd: 0, fontSize: 10 }}>chưa có ảnh</Tag></Tooltip>}
                  </div>
                ))}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );

  // ── XEM ────────────────────────────────────────────────────────────────────
  if (!editing) {
    return (
      <div>
        {rawIdx > 0 && (
          <div style={{ fontSize: 11, color: '#d46b08', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, padding: '4px 6px', marginBottom: 4 }}>
            ⚠️ Prompt này còn <b>{rawIdx}</b> tham chiếu dạng số cứng (<code>@Image1</code>…). Số đó trỏ theo <i>vị trí</i>,
            nên sẽ chỉ sai người khi bank thay đổi. Bấm <b>✨ Dựng prompt từ mô tả</b> để dựng lại, hoặc sửa tay thành chip.
          </div>
        )}
        <div style={{ fontSize: 11, whiteSpace: 'pre-wrap', background: '#fafafa', padding: 6, borderRadius: 4, lineHeight: 2.1 }}>
          {pieces.length === 0 && emptyText ? <span className="muted" style={{ color: '#999' }}>{emptyText}</span> : null}
          {pieces.map((p, i) => {
            if (p.type === 'text') return <span key={i}>{p.text}</span>;
            const known = byId.get(`${p.kind}:${p.id}`);
            const meta = KIND_META[p.kind];
            const color = known ? (known.ready ? COLOR_HEX[meta.color] : '#fa8c16') : '#ff4d4f';
            const chip = (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle',
                margin: '0 2px', padding: known?.previewUrl ? '1px 6px 1px 1px' : '1px 6px',
                border: `1px solid ${color}`, borderRadius: 11, background: `${color}14`,
                fontSize: 11, lineHeight: 1.6, cursor: 'default', maxWidth: 230,
              }}>
                {known?.previewUrl
                  // Mặt người → tròn (đọc ra ngay là ai). Bối cảnh/đạo cụ là ảnh NGANG — cắt tròn thì mất hai bên.
                  ? <img src={known.previewUrl} alt=""
                      style={{ width: 18, height: 18, borderRadius: p.kind === 'char' ? '50%' : 3, objectFit: 'cover', flexShrink: 0, background: '#fff' }} />
                  : <span style={{ fontSize: 10 }}>{meta.icon}</span>}
                <span style={{ color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.label}</span>
              </span>
            );
            return known?.previewUrl ? (
              <Popover key={i} placement="top" mouseEnterDelay={0.25}
                content={
                  <div style={{ maxWidth: 320 }}>
                    <img src={known.previewUrl} alt={known.label}
                      style={{ maxWidth: 320, maxHeight: 320, objectFit: 'contain', display: 'block', borderRadius: 4, background: '#f5f5f5' }} />
                    <div style={{ marginTop: 6, fontSize: 11 }}>
                      <b>{known.label}</b> <Text type="secondary">· {meta.label}</Text>
                      {!known.ready && <div style={{ color: '#d46b08' }}>⚠️ chưa có ảnh gán cho engine — khi gen chỉ còn mô tả bằng chữ</div>}
                    </div>
                  </div>
                }>
                {chip}
              </Popover>
            ) : (
              <Tooltip key={i} title={known
                ? `${meta.label}: ${known.label}${known.ready ? '' : ' — chưa có ảnh, khi gen sẽ chỉ còn mô tả bằng chữ'}`
                : 'Không tìm thấy trong bank — thực thể có thể đã bị xoá'}>
                {chip}
              </Tooltip>
            );
          })}
        </div>
        {readOnly ? null : <Button size="small" style={{ marginTop: 4 }} onClick={() => { setDirty(false); setEditing(true); }}>✏️ Sửa prompt</Button>}
      </div>
    );
  }

  // ── SỬA ────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div
        ref={edRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => { setDirty(true); if (inline && edRef.current) onChange?.(domToText(edRef.current)); }}
        onKeyUp={rememberCaret}
        onMouseUp={rememberCaret}
        onBlur={rememberCaret}
        onKeyDown={(e) => {
          if (e.key === '@') setTimeout(() => { rememberCaret(); setPickerOpen(true); }, 0);
        }}
        // Paste PHẢI về plain text: dán từ Word/web mang theo cả cây HTML, vào contentEditable là
        // vỡ hết định dạng và có thể lôi theo thẻ lạ mà `domToText` không hiểu.
        onPaste={(e) => {
          e.preventDefault();
          const t = e.clipboardData.getData('text/plain');
          const sel = window.getSelection();
          if (!sel || !sel.rangeCount) return;
          const r = sel.getRangeAt(0);
          r.deleteContents();
          // Giữ xuống dòng của đoạn vừa dán.
          const frag = document.createDocumentFragment();
          t.split('\n').forEach((line, i) => {
            if (i) frag.appendChild(document.createElement('br'));
            frag.appendChild(document.createTextNode(line));
          });
          r.insertNode(frag);
          sel.collapseToEnd();
          setDirty(true);
          if (inline && edRef.current) onChange?.(domToText(edRef.current));
        }}
        style={{
          fontSize: 11, lineHeight: 2.1, whiteSpace: 'pre-wrap', background: '#fff',
          border: '1px solid #d9d9d9', borderRadius: 4, padding: '6px 8px',
          minHeight: minHeight ?? 180, maxHeight: 480, overflowY: 'auto', outline: 'none',
        }}
      />
      <Space style={{ marginTop: 6 }} size={6} wrap>
        <Popover open={pickerOpen} onOpenChange={(o) => { if (o) rememberCaret(); setPickerOpen(o); }}
          trigger="click" content={picker} placement="topLeft" title="Chèn tham chiếu">
          <Button size="small" onMouseDown={rememberCaret}>@ Chèn tham chiếu</Button>
        </Popover>
        {inline ? null : (
          <>
            <Button size="small" type="primary" disabled={!dirty}
              onClick={() => { const t = edRef.current ? domToText(edRef.current) : value; onSave?.(t); setEditing(false); }}>
              Lưu
            </Button>
            <Button size="small" onClick={() => setEditing(false)}>Huỷ</Button>
          </>
        )}
        <Text type="secondary" style={{ fontSize: 10 }}>
          Gõ <b>@</b> để chèn · chip xoá nguyên khối bằng Backspace · số <code>@ImageN</code> được điền lúc gen
        </Text>
      </Space>
    </div>
  );
};

/** Node neo của một Range — dùng để kiểm caret đã lưu có còn nằm trong editor không. */
function range0Node(r: Range): Node { return r.startContainer; }

/** Range đặt ở cuối nội dung — dùng khi chưa từng có caret (vừa mở editor đã bấm chèn). */
function endRange(root: HTMLElement): Range {
  const r = document.createRange();
  r.selectNodeContents(root);
  r.collapse(false);
  return r;
}

export default PromptChipEditor;
